import { Plus, X } from "lucide-react";

/**
 * Step 1 — "Questions": 3 open-ended qualitative slots, up to 3 optional
 * screener questions (with knockout logic), and up to 5 multiple-choice
 * questions. All mutation logic stays in the parent and is passed in as
 * callbacks; this component is pure layout.
 */
export default function StepQuestions({
  form, setForm,
  addScreener, updateScreener, removeScreener,
  addMc, updateMc, removeMc,
}) {
  return (
    <div className="space-y-8" data-testid="step-questions">
      {/* Qualitative slots */}
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold mb-3">3 Open-ended Qualitative Questions</div>
        <div className="space-y-3">
          {form.qualitative_questions.map((q, i) => (
            <textarea
              key={`qual-slot-${i}`}
              rows={2}
              value={q}
              onChange={(e) => {
                const next = [...form.qualitative_questions]; next[i] = e.target.value;
                setForm({ ...form, qualitative_questions: next });
              }}
              className="w-full bg-white border border-[var(--rl-border)] px-4 py-3 focus:outline-none focus:border-[var(--rl-brand)]"
              placeholder={`Open-ended question #${i + 1}`}
              data-testid={`input-qual-${i}`}
            />
          ))}
        </div>
      </div>

      {/* Screener questions */}
      <div data-testid="screener-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold">Screener Questions <span className="text-[var(--rl-text-secondary)] normal-case tracking-normal">(optional, max 3)</span></div>
            <div className="text-[11px] text-[var(--rl-text-secondary)] mt-1">Disqualified panelists are routed out before the paid interview — no slot consumed.</div>
          </div>
          <button type="button" onClick={addScreener} disabled={form.screener_questions.length >= 3} className="text-xs font-semibold text-[var(--rl-brand)] disabled:opacity-40" data-testid="add-screener-btn">
            <Plus className="w-3 h-3 inline" /> Add screener
          </button>
        </div>
        {form.screener_questions.length === 0 ? (
          <div className="border border-dashed border-[var(--rl-border)] p-4 text-[11px] text-[var(--rl-text-secondary)] uppercase tracking-[0.2em] font-bold">
            No screeners — every respondent qualifies.
          </div>
        ) : (
          <div className="space-y-4">
            {form.screener_questions.map((sc, i) => (
              <div key={sc._uid || `sc-legacy-${i}`} className="border border-[var(--rl-border)] p-4" data-testid={`screener-block-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    value={sc.question_text}
                    onChange={(e) => updateScreener(i, { question_text: e.target.value })}
                    className="flex-1 bg-white border border-[var(--rl-border)] px-3 py-2 focus:outline-none focus:border-[var(--rl-brand)] text-sm"
                    placeholder={`Screener question #${i + 1}`}
                    data-testid={`input-screener-text-${i}`}
                  />
                  <button type="button" onClick={() => removeScreener(i)} className="text-[var(--rl-text-secondary)] hover:text-[var(--rl-accent)]" data-testid={`remove-screener-${i}`}><X className="w-4 h-4" /></button>
                </div>
                <div className="space-y-2">
                  {sc.options.map((opt, j) => (
                    <div key={`${sc._uid || i}-opt-${j}`} className="flex items-center gap-2">
                      <input
                        value={opt.text}
                        onChange={(e) => {
                          const opts = [...sc.options]; opts[j] = { ...opts[j], text: e.target.value };
                          updateScreener(i, { options: opts });
                        }}
                        className="flex-1 bg-white border border-[var(--rl-border)] px-3 py-2 focus:outline-none focus:border-[var(--rl-brand)] text-sm"
                        placeholder={`Option ${j + 1}`}
                        data-testid={`input-screener-option-${i}-${j}`}
                      />
                      <label className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!opt.disqualify}
                          onChange={(e) => {
                            const opts = [...sc.options]; opts[j] = { ...opts[j], disqualify: e.target.checked };
                            updateScreener(i, { options: opts });
                          }}
                          className="accent-[var(--rl-accent)]"
                          data-testid={`screener-dq-${i}-${j}`}
                        />
                        <span className={opt.disqualify ? "text-[var(--rl-accent)]" : "text-[var(--rl-text-secondary)]"}>Knockout</span>
                      </label>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => updateScreener(i, { options: [...sc.options, { text: "", disqualify: false }] })}
                    className="text-xs font-semibold text-[var(--rl-brand)]"
                    data-testid={`add-screener-option-${i}`}
                  >+ Add option</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Multiple-choice questions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-[0.2em] font-bold">Multiple-Choice Questions (up to 5)</div>
          <button type="button" onClick={addMc} disabled={form.multiple_choice_questions.length >= 5} className="text-xs font-semibold text-[var(--rl-brand)] disabled:opacity-40" data-testid="add-mc-btn">
            <Plus className="w-3 h-3 inline" /> Add
          </button>
        </div>
        <div className="space-y-4">
          {form.multiple_choice_questions.map((mc, i) => (
            <div key={mc._uid || `mc-legacy-${i}`} className="border border-[var(--rl-border)] p-4" data-testid={`mc-block-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <input
                  value={mc.question_text}
                  onChange={(e) => updateMc(i, { question_text: e.target.value })}
                  className="flex-1 bg-white border border-[var(--rl-border)] px-3 py-2 focus:outline-none focus:border-[var(--rl-brand)] text-sm"
                  placeholder={`Multiple-choice question #${i + 1}`}
                  data-testid={`input-mc-text-${i}`}
                />
                <button type="button" onClick={() => removeMc(i)} className="text-[var(--rl-text-secondary)] hover:text-[var(--rl-accent)]"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-2">
                {mc.options.map((opt, j) => (
                  <input
                    key={`${mc._uid || i}-opt-${j}`}
                    value={opt}
                    onChange={(e) => {
                      const opts = [...mc.options]; opts[j] = e.target.value;
                      updateMc(i, { options: opts });
                    }}
                    className="w-full bg-white border border-[var(--rl-border)] px-3 py-2 focus:outline-none focus:border-[var(--rl-brand)] text-sm"
                    placeholder={`Option ${j + 1}`}
                    data-testid={`input-mc-option-${i}-${j}`}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => updateMc(i, { options: [...mc.options, ""] })}
                  className="text-xs font-semibold text-[var(--rl-brand)]"
                  data-testid={`add-option-${i}`}
                >+ Add option</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
